import { useEffect, useRef, useState } from 'react';
import { FileText, Loader2, Lock, Paperclip, Send, X } from 'lucide-react';
import FileDropzone from '../FileDropzone';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';
import {
  ATTACH_ACCEPT, downloadFromLink, formatFileSize, formatMessageTime, toastApiError,
} from '../../lib/support';

/**
 * One support conversation: its messages, then a reply box. Help (the
 * customer) and Support (us) both render this; what differs is `mode`.
 *
 * - customer: their messages are "You", ours are "No Half Days". Send only.
 * - admin: their messages carry their name, ours the admin's, and internal
 *   notes show dashed. The reply box can write a note instead, and choose
 *   where the conversation goes after sending.
 *
 * `onSend({ message, internal, status, files })` resolves true when it was
 * saved, which clears the box. `getFileLink(fileId)` resolves a short-lived
 * download URL, which is followed in place (downloadFromLink), never in a new
 * window. Files are never internal: attaching turns the note off.
 */
export function Conversation({ messages, mode, customerName, region, onSend, getFileLink, closed = false }) {
  const admin = mode === 'admin';
  const [draft, setDraft] = useState('');
  const [internal, setInternal] = useState(false);
  const [sending, setSending] = useState(null);
  const [files, setFiles] = useState([]);
  const [attaching, setAttaching] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const end = useRef(null);

  // Keep the newest message in view as the thread grows
  useEffect(() => {
    end.current?.scrollIntoView?.({ block: 'nearest' });
  }, [messages.length]);

  const canSend = (draft.trim() || files.length) && !sending;

  const send = async (status) => {
    if (!canSend) return;
    setSending(status || 'send');
    const ok = await onSend({ message: draft, internal: admin && internal && !files.length, status, files });
    setSending(null);
    if (ok) {
      setDraft('');
      setInternal(false);
      setFiles([]);
      setAttaching(false);
    }
  };

  const download = async (file) => {
    if (downloading) return;
    setDownloading(file.id);
    try {
      downloadFromLink(await getFileLink(file.id));
    } catch (e) {
      toastApiError(e, 'Could not download that file');
    } finally {
      setDownloading(null);
    }
  };

  const author = (m) => {
    if (m.author === 'customer') return admin ? customerName || 'Customer' : 'You';
    if (m.author === 'system') return 'No Half Days';
    return admin ? m.author_name || 'Admin' : 'No Half Days';
  };

  return (
    <div className="space-y-6">
      <ol className="space-y-3" data-testid="conversation-messages">
        {messages.map((m) => {
          const ours = m.author !== 'customer';
          return (
            <li
              key={m.id}
              className={cn(
                'rounded-cards p-4 sm:p-5 min-w-0',
                m.internal
                  ? 'border border-dashed border-ochre bg-ochre-soft/40'
                  : ours ? 'bg-surface' : 'bg-mist'
              )}
              data-testid={m.internal ? 'message-internal' : `message-${m.author}`}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 mb-2">
                <span className="font-medium inline-flex items-center gap-2">
                  {m.internal && <Lock className="w-3.5 h-3.5 text-ochre" aria-hidden="true" />}
                  {author(m)}
                  {m.internal && <span className="eyebrow">internal note</span>}
                </span>
                <span className="text-sm text-muted-foreground tabular-nums">
                  {formatMessageTime(m.created_at, region)}
                  {m.channel === 'email' && ' · by email'}
                </span>
              </div>
              <p className="whitespace-pre-wrap break-words leading-relaxed">{m.body}</p>
              {m.files?.length > 0 && (
                <ul className="mt-3 flex flex-wrap gap-2" data-testid="message-files">
                  {m.files.map((f) => (
                    <li key={f.id} className="min-w-0 max-w-full">
                      <button
                        type="button"
                        onClick={() => download(f)}
                        aria-busy={downloading === f.id}
                        className="flex items-center gap-2 rounded-pill bg-parchment hover:bg-mist px-3 h-9 text-sm min-w-0 max-w-full"
                        title={`Download ${f.filename}`}
                        data-testid="message-file"
                      >
                        {downloading === f.id
                          ? <Loader2 className="w-4 h-4 shrink-0 animate-spin text-muted-foreground" aria-hidden="true" />
                          : <FileText className="w-4 h-4 shrink-0 text-muted-foreground" aria-hidden="true" />}
                        <span className="truncate">{f.filename}</span>
                        <span className="text-muted-foreground tabular-nums shrink-0">{formatFileSize(f.size)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ol>
      <div ref={end} />

      <div className={cn('rounded-cards p-4 sm:p-5 space-y-3', admin && internal ? 'bg-ochre-soft/40 border border-dashed border-ochre' : 'bg-surface')}>
        <Label htmlFor="support-reply" className="sr-only">
          {admin && internal ? 'Internal note' : 'Reply'}
        </Label>
        <Textarea
          id="support-reply"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send();
          }}
          placeholder={
            admin
              ? internal ? 'A note only the team sees' : `Reply to ${customerName || 'them'}`
              : closed ? 'Write to reopen this conversation' : 'Write a reply'
          }
          rows={4}
          className="bg-parchment"
          data-testid="support-reply-input"
        />
        {attaching && (
          <FileDropzone
            multiple
            compact
            accept={ATTACH_ACCEPT}
            title="Choose files, or drop them here"
            onSelect={(chosen) => setFiles((prev) => [...prev, ...chosen].slice(0, 10))}
            data-testid="support-attach-drop"
          />
        )}
        {files.length > 0 && (
          <ul className="flex flex-wrap gap-2" data-testid="support-attached">
            {files.map((f, i) => (
              <li key={`${f.name}-${i}`} className="flex items-center gap-2 rounded-pill bg-mist pl-3 pr-1 h-9 text-sm min-w-0 max-w-full">
                <span className="truncate">{f.name}</span>
                <span className="text-muted-foreground tabular-nums shrink-0">{formatFileSize(f.size)}</span>
                <button
                  type="button"
                  onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                  className="w-7 h-7 rounded-full hover:bg-parchment flex items-center justify-center shrink-0"
                  aria-label={`Remove ${f.name}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => { setAttaching((a) => !a); setInternal(false); }}
              aria-expanded={attaching}
              data-testid="support-attach"
            >
              <Paperclip />
              Attach files
            </Button>
            {admin ? (
              <div className="flex items-center gap-2">
                <Switch
                  id="support-internal"
                  checked={internal && !files.length}
                  disabled={files.length > 0}
                  onCheckedChange={setInternal}
                  data-testid="support-internal-toggle"
                />
                <Label htmlFor="support-internal" className="font-normal">Internal note</Label>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">We&rsquo;ll email you when we reply.</p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            {admin && !internal && (
              <Button variant="outline" onClick={() => send('closed')} disabled={!canSend} data-testid="support-send-close">
                {sending === 'closed' && <Loader2 className="animate-spin" />}
                Send and close
              </Button>
            )}
            <Button onClick={() => send()} disabled={!canSend} data-testid="support-send">
              {sending === 'send' ? <Loader2 className="animate-spin" /> : <Send />}
              {admin && internal ? 'Add note' : 'Send'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
